/**
 * Sign-in (FR-ORG-03): e-mail + password, then the authenticator code for privileged
 * roles (admins, records officers, auditors). First sign-in: choose a password, then (where required) enrol an
 * authenticator; recovery codes are shown once. 5 failed attempts lock the account
 * for 15 minutes — the screen says so plainly and when to try again.
 */
import { useMutation } from '@tanstack/react-query';
import { Lock, LogIn } from 'lucide-react';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Button, ErrorNote, Input, Note } from '../../components/ui';
import { BRAND } from '../../config/brand';
import { ApiError, api, deviceKey } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { EP } from '../../lib/endpoints';
import { time } from '../../lib/format';
import type { LoginResponse, SessionResponse } from '../../lib/types';

interface Setup {
  secret: string;
  otpauthUri: string;
  qrDataUrl: string;
}

/**
 * The sign-in card is a library catalogue card: a typed line across the top, the heading
 * above the red rule, the rod hole punched at the bottom (styles.css .catalog-card).
 */
export function Frame({ title, sub, children }: { title: string; sub?: ReactNode; children: ReactNode }) {
  const today = new Intl.DateTimeFormat('en-PH', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Manila' }).format(new Date());
  const L = BRAND.landing;
  // Boss TG 1356: no "QXM" on the landing page, the browser tab included.
  useEffect(() => {
    document.title = L.appName;
  }, [L.appName]);
  return (
    <div className="grid min-h-dvh bg-paper lg:grid-cols-[minmax(0,1.15fr)_minmax(460px,1fr)]">
      {/* Landing hero (desktop): the standard the product supports, over the education photo. */}
      {L.title && (
        <aside aria-label={`About ${L.appName}`} className="relative hidden overflow-hidden bg-[#0f1a2e] lg:block">
          <img src={L.heroUrl} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover object-right" />
          <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-[#0b1424]/95 via-[#0b1424]/75 to-[#0b1424]/10" />
          <div className="relative flex h-full max-w-[620px] flex-col justify-center px-12 py-16 text-white xl:px-16">
            <p className="code text-sm font-semibold uppercase tracking-[0.18em] text-[#7fe0d4]">{L.eyebrow}</p>
            <h2 className="mt-3 text-4xl font-bold leading-tight xl:text-5xl">{L.title}</h2>
            <p className="mt-3 text-2xl font-semibold text-white/90">{L.subtitle}</p>
            <p className="mt-6 max-w-[520px] text-base leading-relaxed text-white/80">{L.body}</p>
          </div>
        </aside>
      )}
      <div className="grid place-items-center px-4 py-10">
        <main className="w-full max-w-[420px]">
          <div className="mb-6 flex justify-center">
            <img src={L.logoLight} alt={L.appName} className="h-16 w-auto dark:hidden" />
            <img src={L.logoDark} alt={L.appName} className="hidden h-16 w-auto dark:block" />
          </div>
          {/* Phones and tablets: the same headline, short, above the card. */}
          {L.title && (
            <div className="mb-5 text-center lg:hidden">
              <p className="code text-xs font-semibold uppercase tracking-[0.16em] text-brand">{L.eyebrow}</p>
              <p className="mt-1 text-base font-bold leading-snug">{L.title}</p>
              <p className="text-sm text-muted">{L.subtitle}</p>
            </div>
          )}
          <div className="rounded-[4px] [filter:drop-shadow(0_1px_1px_rgb(var(--shadow)/0.10))_drop-shadow(0_10px_24px_rgb(var(--shadow)/0.10))]">
            <div className="catalog-card rounded-[4px] border border-line bg-card px-6 pb-12 pt-4 sm:px-8">
              <div className="code flex justify-between gap-3 text-[13px] text-muted" aria-hidden>
                <span>Staff access</span>
                <span>{today}</span>
              </div>
              <h1 className="catalog-rule pb-2 pt-1 text-2xl font-bold leading-tight">{title}</h1>
              {sub && <div className="mt-3 text-sm text-muted">{sub}</div>}
              <div className="mt-6">{children}</div>
            </div>
          </div>
          <footer className="mt-8 flex flex-col items-center gap-3 text-center">
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted">In partnership with</span>
              <span className="rounded-[4px] bg-white px-2.5 py-1.5 shadow-sm ring-1 ring-black/5">
                <img src={L.partnerLogo} alt={L.partnerName} className="h-12 w-auto" />
              </span>
            </div>
            <p className="text-xs tracking-wide text-muted">
              Powered by <span className="font-semibold text-ink">{L.poweredBy}</span>
            </p>
          </footer>
        </main>
      </div>
    </div>
  );
}

const codeInput = (v: string) => v.replace(/[^\dA-Za-z-]/g, '').slice(0, 20);

/** Lockout (HTTP 429, code LOCKED + lockedUntil) gets its own message. 423 is LEGAL_HOLD, never a lockout. */
function LoginError({ error }: { error: unknown }) {
  if (error instanceof ApiError && (error.code === 'LOCKED')) {
    const until = error.body.lockedUntil as string | undefined;
    return (
      <Note tone="danger" icon={<Lock aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />}>
        <p className="font-semibold">This account is locked for 15 minutes</p>
        <p className="mt-0.5">
          Too many wrong passwords. {until ? `Try again after ${time(until)}.` : 'Try again later.'} If it was not you, tell your administrator.
        </p>
      </Note>
    );
  }
  return <ErrorNote error={error} />;
}

export function SignIn() {
  const { adopt, endedReason } = useAuth();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [code, setCode] = useState('');

  const signIn = useMutation({
    mutationFn: () => api<LoginResponse>(EP.auth.login, { method: 'POST', body: { login: login.trim(), password, deviceKey: deviceKey() }, anonymous: true }),
    onSuccess: (r) => (r.mfaRequired ? setMfaToken(r.mfaToken) : adopt(r)),
  });
  const verify = useMutation({
    mutationFn: () => api<SessionResponse>(EP.auth.mfa, { method: 'POST', body: { mfaToken, code: code.trim() }, anonymous: true }),
    onSuccess: adopt,
  });

  if (mfaToken) {
    return (
      <Frame title="Enter your authenticator code" sub="Open your authenticator app and type the 6-digit code. Lost your phone? Use one of your recovery codes.">
        <form onSubmit={(e: FormEvent) => { e.preventDefault(); verify.mutate(); }} className="space-y-4">
          <Input label="Code" autoFocus inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(codeInput(e.target.value))} placeholder="123456" className="[&_input]:code [&_input]:text-center [&_input]:text-xl [&_input]:tracking-[0.3em]" />
          <LoginError error={verify.error} />
          <Button type="submit" size="lg" block loading={verify.isPending} disabled={code.length < 6}>
            Verify
          </Button>
          <Button variant="quiet" block onClick={() => { setMfaToken(null); setCode(''); verify.reset(); }}>
            Use a different account
          </Button>
        </form>
      </Frame>
    );
  }

  return (
    <Frame title="Sign in">
      {endedReason === 'idle' && (
        <Note tone="info" className="mb-4">
          You were signed out after a period of no activity. Sign in again to continue.
        </Note>
      )}
      {endedReason === 'expired' && (
        <Note tone="info" className="mb-4">
          Your session ended. Sign in again to continue.
        </Note>
      )}
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); signIn.mutate(); }} className="space-y-4">
        <Input label="E-mail" type="email" autoComplete="username" autoCapitalize="none" autoFocus value={login} onChange={(e) => setLogin(e.target.value)} />
        <Input label="Password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        <LoginError error={signIn.error} />
        <Button type="submit" size="lg" block loading={signIn.isPending} disabled={!login || !password} icon={<LogIn aria-hidden className="h-4 w-4" />}>
          Sign in
        </Button>
      </form>
      <p className="mt-5 text-center text-sm text-muted">Forgot your password? Ask your organisation’s administrator to reset it.</p>
    </Frame>
  );
}

export function PasswordForm({ onDone, submitLabel = 'Save password', temporary }: { onDone: () => void; submitLabel?: string; temporary?: boolean }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const change = useMutation({
    mutationFn: () => api<{ ok: boolean; message: string }>(EP.auth.password, { method: 'POST', body: { currentPassword: current, newPassword: next } }),
    onSuccess: onDone,
  });
  const mismatch = again.length > 0 && next !== again;
  return (
    <form onSubmit={(e) => { e.preventDefault(); change.mutate(); }} className="space-y-4">
      <Input label={temporary ? 'Temporary password' : 'Current password'} type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
      <Input label="New password" type="password" autoComplete="new-password" hint="At least 10 characters. A short sentence is easy to remember and hard to guess." value={next} onChange={(e) => setNext(e.target.value)} error={change.error instanceof ApiError ? change.error.fields.newPassword : undefined} />
      <Input label="New password again" type="password" autoComplete="new-password" value={again} error={mismatch ? 'The passwords do not match.' : undefined} onChange={(e) => setAgain(e.target.value)} />
      {!(change.error instanceof ApiError && change.error.fields.newPassword) && <ErrorNote error={change.error} />}
      <Button type="submit" size="lg" block loading={change.isPending} disabled={!current || next.length < 10 || next !== again}>
        {submitLabel}
      </Button>
    </form>
  );
}

/** Forced on first sign-in or after an admin reset. */
export function SetPassword() {
  const { user, signOut } = useAuth();
  return (
    <Frame title="Choose your password" sub={`${user?.name ?? ''}, replace the temporary password with your own. You will then sign in again.`}>
      <PasswordForm temporary onDone={() => void signOut()} />
      <Button variant="quiet" block className="mt-2" onClick={() => void signOut()}>
        Sign out
      </Button>
    </Frame>
  );
}

/** Authenticator enrolment, required for admins, records officers and auditors. */
export function MfaSetup() {
  const { adopt, signOut } = useAuth();
  const [code, setCode] = useState('');
  const [result, setResult] = useState<({ recoveryCodes: string[] } & SessionResponse) | null>(null);
  const setup = useMutation({ mutationFn: () => api<Setup>(EP.auth.mfaSetup, { method: 'POST', body: {} }) });
  const enable = useMutation({
    mutationFn: () => api<{ recoveryCodes: string[] } & SessionResponse>(EP.auth.mfaEnable, { method: 'POST', body: { code: code.trim() } }),
    onSuccess: (r) => (r.recoveryCodes?.length ? setResult(r) : adopt(r)),
  });

  if (result) {
    const copy = () => void navigator.clipboard?.writeText(result.recoveryCodes.join('\n')).catch(() => undefined);
    return (
      <Frame title="Save your recovery codes" sub="Each code works once if you lose your phone. They are shown only now — keep them away from your phone.">
        <ul className="code grid grid-cols-2 gap-2 rounded-tag border border-line bg-sunken p-3 text-sm">
          {result.recoveryCodes.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
        <div className="mt-4 flex gap-2">
          <Button variant="secondary" size="lg" className="flex-1" onClick={copy}>
            Copy
          </Button>
          <Button size="lg" className="flex-1" onClick={() => adopt(result)}>
            I saved them
          </Button>
        </div>
      </Frame>
    );
  }
  if (!setup.data) {
    return (
      <Frame title="Set up two-step sign-in" sub="Your role can manage people, records or the audit log, so it needs an authenticator app (Google or Microsoft Authenticator). It takes a minute.">
        <ErrorNote error={setup.error} />
        <Button size="lg" block className="mt-2" loading={setup.isPending} onClick={() => setup.mutate()}>
          Start set-up
        </Button>
        <Button variant="quiet" block className="mt-2" onClick={() => void signOut()}>
          Sign out
        </Button>
      </Frame>
    );
  }
  const s = setup.data;
  return (
    <Frame title="Scan with your authenticator" sub="Scan the code, then type the 6 digits the app shows.">
      <img src={s.qrDataUrl} alt="QR code for your authenticator app" width={184} height={184} className="mx-auto rounded-tag border border-line bg-white p-2" />
      <Note tone="neutral" className="mt-3">
        Can’t scan? Enter this key in the app: <code className="code break-all text-ink">{s.secret}</code>
      </Note>
      <form onSubmit={(e) => { e.preventDefault(); enable.mutate(); }} className="mt-4 space-y-4">
        <Input label="6-digit code" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} />
        <ErrorNote error={enable.error} />
        <Button type="submit" size="lg" block loading={enable.isPending} disabled={code.length !== 6}>
          Turn on two-step sign-in
        </Button>
      </form>
    </Frame>
  );
}
